<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Team Report</h1>
    <p class="text-muted mb-0">Monitor your team's certification status and test completion</p>
  </div>
  <!-- Export Dropdown Button -->
  <div class="dropdown">
    <button class="btn btn-outline-secondary dropdown-toggle export-btn" type="button"
            data-bs-toggle="dropdown" aria-expanded="false"
            [disabled]="isLoading || hasError || isExporting || hasNoTeamMembers || hasNoTestsConfigured">
      <i class="bi bi-download me-2"></i><span class="d-none d-sm-inline">Export</span>
      <span *ngIf="isExporting" class="spinner-border spinner-border-sm ms-2" role="status">
        <span class="visually-hidden">Exporting...</span>
      </span>
    </button>
    <ul class="dropdown-menu dropdown-menu-end">
      <li>
        <button class="dropdown-item" (click)="exportCsv()" [disabled]="isExportingCsv">
          <i class="bi bi-file-earmark-spreadsheet me-2"></i>Export as CSV
        </button>
      </li>
      <li><hr class="dropdown-divider"></li>
      <li>
        <button class="dropdown-item" (click)="exportPdfSummary()" [disabled]="isExportingPdf">
          <i class="bi bi-file-earmark-pdf me-2"></i>Export PDF (Summary)
        </button>
      </li>
      <li>
        <button class="dropdown-item" (click)="exportPdfDetailed()" [disabled]="isExportingPdf">
          <i class="bi bi-file-earmark-pdf me-2"></i>Export PDF (Detailed)
        </button>
      </li>
    </ul>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary mb-3" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted">Loading team report...</p>
</div>

<!-- Error State -->
<div *ngIf="hasError && !isLoading" class="card">
  <div class="card-body text-center py-5">
    <i class="bi bi-exclamation-triangle text-danger fs-3rem"></i>
    <h5 class="mt-3">Unable to Load Team Report</h5>
    <p class="text-muted mb-4">{{ errorMessage }}</p>
    <button class="btn btn-primary" (click)="retryLoad()">
      <i class="bi bi-arrow-clockwise me-2"></i>Try Again
    </button>
  </div>
</div>

<!-- No Team Members Empty State -->
<div *ngIf="hasNoTeamMembers" class="card empty-state-no-members">
  <div class="card-body text-center py-5">
    <i class="bi bi-people text-muted fs-3rem"></i>
    <h5 class="mt-3">No team members found</h5>
    <p class="text-muted mb-0">Add team members to view their safety test status</p>
  </div>
</div>

<!-- No Tests Configured Empty State -->
<div *ngIf="hasNoTestsConfigured" class="card empty-state-no-tests">
  <div class="card-body text-center py-5">
    <i class="bi bi-clipboard-x text-muted fs-3rem"></i>
    <h5 class="mt-3">No safety tests configured</h5>
    <p class="text-muted mb-0">Safety tests need to be configured for your organization</p>
  </div>
</div>

<!-- Main Content (shown when not loading and no error and data exists) -->
<ng-container *ngIf="!isLoading && !hasError && !hasNoTeamMembers && !hasNoTestsConfigured">
  <!-- Status Summary Cards -->
  <div class="row g-3 mb-4">
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100 clickable" [class.active]="statusFilter === 'current'"
           (click)="onStatusFilterChange(statusFilter === 'current' ? 'all' : 'current')">
        <div class="card-body p-3">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-success-subtle text-success">
              <i class="bi bi-check-circle"></i>
            </div>
            <div>
              <div class="stat-value text-success">{{ currentCount }}</div>
              <div class="stat-label text-muted">Current</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100 clickable" [class.active]="statusFilter === 'expiring'"
           (click)="onStatusFilterChange(statusFilter === 'expiring' ? 'all' : 'expiring')">
        <div class="card-body p-3">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-warning-subtle text-warning">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div>
              <div class="stat-value text-warning">{{ expiringCount }}</div>
              <div class="stat-label text-muted">Expiring Soon</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100 clickable" [class.active]="statusFilter === 'expired'"
           (click)="onStatusFilterChange(statusFilter === 'expired' ? 'all' : 'expired')">
        <div class="card-body p-3">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-danger-subtle text-danger">
              <i class="bi bi-x-circle"></i>
            </div>
            <div>
              <div class="stat-value text-danger">{{ expiredCount }}</div>
              <div class="stat-label text-muted">Expired</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-sm-6 col-lg-3">
      <div class="card stat-card h-100 clickable" [class.active]="statusFilter === 'incomplete'"
           (click)="onStatusFilterChange(statusFilter === 'incomplete' ? 'all' : 'incomplete')">
        <div class="card-body p-3">
          <div class="d-flex align-items-center gap-3">
            <div class="stat-icon bg-secondary-subtle text-secondary">
              <i class="bi bi-circle-half"></i>
            </div>
            <div>
              <div class="stat-value">{{ incompleteCount }}</div>
              <div class="stat-label text-muted">Incomplete</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Team Members Card -->
  <div class="card">
    <div class="card-header p-3">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0">Team Members</h5>
          <span class="badge bg-secondary">{{ filteredMembers.length }} of {{ teamMembers.length }}</span>
        </div>
        <div class="d-flex flex-column flex-md-row flex-grow-1 gap-2 align-items-md-center team-report-controls">
          <!-- Search -->
          <div class="input-group input-group-sm search-input flex-shrink-1">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input type="text" class="form-control" placeholder="Search members..."
                   [(ngModel)]="searchQuery" (ngModelChange)="onSearchChange()">
            <button *ngIf="searchQuery" class="btn btn-outline-secondary" type="button"
                    (click)="searchQuery = ''; onSearchChange()">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <!-- Status Filter Pills - Horizontal scroll on mobile -->
          <div class="filter-tabs-wrapper flex-shrink-0">
            <div class="btn-group btn-group-sm flex-nowrap text-nowrap filter-tabs" role="group">
              <button type="button" class="btn" [class.btn-primary]="statusFilter === 'all'"
                      [class.btn-outline-secondary]="statusFilter !== 'all'"
                      (click)="onStatusFilterChange('all')">All</button>
              <button type="button" class="btn" [class.btn-success]="statusFilter === 'current'"
                      [class.btn-outline-secondary]="statusFilter !== 'current'"
                      (click)="onStatusFilterChange('current')">Current</button>
              <button type="button" class="btn" [class.btn-warning]="statusFilter === 'expiring'"
                      [class.btn-outline-secondary]="statusFilter !== 'expiring'"
                      (click)="onStatusFilterChange('expiring')">Expiring</button>
              <button type="button" class="btn" [class.btn-danger]="statusFilter === 'expired'"
                      [class.btn-outline-secondary]="statusFilter !== 'expired'"
                      (click)="onStatusFilterChange('expired')">Expired</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body p-0">
      <!-- Filtered Empty State (when search/filter returns no results) -->
      <div *ngIf="hasFilteredEmptyState" class="text-center py-5 empty-state-filtered">
        <i class="bi bi-search text-muted fs-3rem"></i>
        <h5 class="mt-3">No members match your search</h5>
        <p class="text-muted mb-3">Try adjusting your search or filters to find what you're looking for</p>
        <button class="btn btn-outline-primary" (click)="clearFilters()">
          <i class="bi bi-x-circle me-2"></i>Clear Filters
        </button>
      </div>

      <!-- Member List -->
      <div class="member-list" *ngIf="filteredMembers.length > 0">
        <div *ngFor="let member of filteredMembers" class="member-item"
             [class.expanded]="isMemberExpanded(member.id)">
          <!-- Member Row -->
          <div class="member-row" (click)="toggleMemberDetails(member.id)">
            <div class="d-flex align-items-center gap-3 flex-grow-1">
              <div class="member-avatar">
                {{ member.name.charAt(0) }}
              </div>
              <div class="member-info flex-grow-1">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="fw-medium">{{ member.name }}</span>
                  <span class="badge" [ngClass]="getCertStatusClass(member.overallStatus)">
                    {{ getCertStatusText(member.overallStatus) }}
                  </span>
                </div>
                <div class="small text-muted">{{ member.role }}</div>
              </div>
            </div>

            <!-- Test Status Indicators (Hidden on mobile collapsed view) -->
            <div class="test-status-row d-none d-md-flex">
              <div *ngFor="let test of member.testResults" class="test-indicator-compact"
                   [ngClass]="getTestStatusClass(test.status)" [title]="test.testName">
                <i class="bi" [ngClass]="getTestStatusIcon(test.status)"></i>
                <span class="test-label">{{ getTestDisplayName(test) }}</span>
              </div>
            </div>

            <!-- Expand Icon -->
            <div class="expand-icon">
              <i class="bi" [class.bi-chevron-down]="!isMemberExpanded(member.id)"
                 [class.bi-chevron-up]="isMemberExpanded(member.id)"></i>
            </div>
          </div>

          <!-- Expanded Details -->
          <div class="member-details" *ngIf="isMemberExpanded(member.id)">
            <!-- Mobile Quick Status Icons (visible in expanded view on mobile) -->
            <div class="test-status-row-mobile d-flex d-md-none flex-wrap mb-4">
              <div *ngFor="let test of member.testResults" class="test-indicator-compact"
                   [ngClass]="getTestStatusClass(test.status)" [title]="test.testName">
                <i class="bi" [ngClass]="getTestStatusIcon(test.status)"></i>
                <span class="test-label">{{ getTestDisplayName(test) }}</span>
              </div>
            </div>

            <div class="row g-4">
              <!-- Left Column: Member Info -->
              <div class="col-lg-4">
                <div class="detail-section">
                  <h6 class="detail-section-title">
                    <i class="bi bi-person me-2"></i>Contact Information
                  </h6>
                  <div class="detail-list">
                    <div class="detail-item">
                      <span class="detail-label">Email</span>
                      <span class="detail-value">{{ member.email }}</span>
                    </div>
                    <div class="detail-item" *ngIf="member.phone">
                      <span class="detail-label">Phone</span>
                      <span class="detail-value">{{ member.phone | phoneFormat }}</span>
                    </div>
                    <div class="detail-item" *ngIf="member.hireDate">
                      <span class="detail-label">Hire Date</span>
                      <span class="detail-value">{{ member.hireDate | adnDate }}</span>
                    </div>
                  </div>
                </div>

                <div class="detail-section mt-3">
                  <h6 class="detail-section-title">
                    <i class="bi bi-bar-chart me-2"></i>Completion
                  </h6>
                  <div class="completion-stat">
                    <div class="d-flex justify-content-between mb-1">
                      <span class="small">{{ getMemberPassedCount(member) }} of {{ getMemberTotalTests(member) }} tests passed</span>
                      <span class="small fw-medium">{{ getCompletionPercentage(member) }}%</span>
                    </div>
                    <div class="progress h-8px">
                      <div class="progress-bar bg-success" [style.width.%]="getCompletionPercentage(member)"></div>
                    </div>
                  </div>
                </div>

                <div class="detail-section mt-3">
                  <h6 class="detail-section-title">
                    <i class="bi bi-lightning me-2"></i>Actions
                  </h6>
                  <div class="d-flex flex-column gap-2">
                    <button class="btn btn-sm btn-outline-primary action-btn" (click)="sendReminder(member); $event.stopPropagation()"
                            [disabled]="member.overallStatus === 'current'">
                      <i class="bi bi-bell me-2"></i>Send Reminder
                    </button>
                  </div>
                </div>
              </div>

              <!-- Right Column: Test Details -->
              <div class="col-lg-8">
                <div class="detail-section">
                  <h6 class="detail-section-title">
                    <i class="bi bi-clipboard-check me-2"></i>Test Status
                  </h6>
                  <div class="test-details-list">
                    <div *ngFor="let test of member.testResults" class="test-detail-card"
                         [class.test-passed]="test.status === 'passed'"
                         [class.test-expired]="test.status === 'expired'"
                         [class.test-expiring]="test.status === 'expiring_soon'"
                         [class.test-failed]="test.status === 'failed'"
                         [class.test-not-started]="test.status === 'not_started'"
                         [class.test-in-progress]="test.status === 'in_progress'">
                      <div class="test-detail-header">
                        <div class="d-flex align-items-center gap-2">
                          <i class="bi" [ngClass]="getTestStatusIcon(test.status)"></i>
                          <span class="fw-medium">{{ test.testName }}</span>
                        </div>
                        <span class="badge" [ngClass]="{
                          'bg-success': test.status === 'passed',
                          'bg-danger': test.status === 'failed',
                          'bg-warning text-dark': test.status === 'expired' || test.status === 'expiring_soon',
                          'bg-secondary': test.status === 'not_started',
                          'bg-info': test.status === 'in_progress'
                        }">
                          {{ getTestStatusText(test.status) }}
                        </span>
                      </div>

                      <div class="test-detail-body" *ngIf="test.status !== 'not_started' && test.status !== 'in_progress'">
                        <div class="row g-3">
                          <div class="col-12 col-sm-6 col-lg-3" *ngIf="test.score !== null">
                            <div class="test-stat">
                              <span class="test-stat-value" [class.text-success]="test.score >= test.passingScore"
                                    [class.text-danger]="test.score < test.passingScore">
                                {{ test.score }}%
                              </span>
                              <span class="test-stat-label">Score</span>
                            </div>
                          </div>
                          <div class="col-12 col-sm-6 col-lg-3">
                            <div class="test-stat">
                              <span class="test-stat-value">{{ test.passingScore }}%</span>
                              <span class="test-stat-label">Passing</span>
                            </div>
                          </div>
                          <div class="col-12 col-sm-6 col-lg-3" *ngIf="test.completedAt">
                            <div class="test-stat">
                              <span class="test-stat-value">{{ test.completedAt | adnDate }}</span>
                              <span class="test-stat-label">Completed</span>
                            </div>
                          </div>
                          <div class="col-12 col-sm-6 col-lg-3" *ngIf="test.expiresAt">
                            <div class="test-stat">
                              <span class="test-stat-value"
                                    [class.text-danger]="isExpired(test.expiresAt)"
                                    [class.text-warning]="isExpiringSoon(test.expiresAt) && !isExpired(test.expiresAt)">
                                {{ test.expiresAt | adnDate }}
                              </span>
                              <span class="test-stat-label">
                                {{ isExpired(test.expiresAt) ? 'Expired' : 'Expires' }}
                                <span *ngIf="!isExpired(test.expiresAt)" class="text-muted">
                                  ({{ getDaysUntilExpiration(test.expiresAt) }} days)
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                        <div class="mt-2 small text-muted" *ngIf="test.attempts > 0">
                          <i class="bi bi-arrow-repeat me-1"></i>{{ test.attempts }} attempt{{ test.attempts > 1 ? 's' : '' }}
                        </div>
                      </div>

                      <div class="test-detail-body" *ngIf="test.status === 'not_started'">
                        <p class="text-muted small mb-0">
                          This test has not been started yet. Passing score: {{ test.passingScore }}%
                        </p>
                      </div>

                      <div class="test-detail-body" *ngIf="test.status === 'in_progress'">
                        <p class="text-muted small mb-0">
                          This test is currently in progress. Passing score: {{ test.passingScore }}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Legend -->
  <div class="card mt-4">
    <div class="card-body py-3">
      <div class="d-flex flex-wrap gap-3 gap-md-4 small legend-items">
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-check-circle-fill text-success"></i>
          <span class="text-muted">Passed</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-exclamation-triangle-fill text-warning"></i>
          <span class="text-muted">Expiring Soon</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-exclamation-circle-fill text-warning"></i>
          <span class="text-muted">Expired</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-x-circle-fill text-danger"></i>
          <span class="text-muted">Failed</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-hourglass-split text-info"></i>
          <span class="text-muted">In Progress</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <i class="bi bi-circle text-secondary"></i>
          <span class="text-muted">Not Started</span>
        </div>
      </div>
    </div>
  </div>
</ng-container>
